import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Rocket, Upload, ExternalLink, Building2, Globe, MapPin,
  FileText, Plus, Search, Star, ArrowUpRight, Landmark,
  BriefcaseBusiness, TrendingUp, Users,
} from "lucide-react";
import PreSeedFundsSection from "@/components/sales/PreSeedFundsSection";

/* ───── static data ───── */

const PLATFORMS = [
  { name: "Crowdcube", url: "https://www.crowdcube.com", type: "Equity Crowdfunding", scope: "🇪🇺 Europe", description: "Levée de fonds participative en equity" },
  { name: "Seedrs", url: "https://www.seedrs.com", type: "Equity Crowdfunding", scope: "🇪🇺 Europe", description: "Plateforme européenne d'investissement en startups" },
  { name: "WiSEED", url: "https://www.wiseed.com", type: "Equity Crowdfunding", scope: "🇫🇷 France", description: "Crowdfunding immobilier et startups françaises" },
  { name: "Anaxago", url: "https://www.anaxago.com", type: "Equity Crowdfunding", scope: "🇫🇷 France", description: "Plateforme d'investissement immobilier et startup" },
  { name: "KissKissBankBank", url: "https://www.kisskissbankbank.com", type: "Reward Crowdfunding", scope: "🇫🇷 France", description: "Financement participatif pour projets créatifs" },
  { name: "Ulule", url: "https://www.ulule.com", type: "Reward Crowdfunding", scope: "🇪🇺 Europe", description: "Leader européen du financement participatif" },
];

const INCUBATORS = [
  { name: "Y Combinator", url: "https://www.ycombinator.com", location: "San Francisco, USA", description: "Le plus célèbre accélérateur au monde. Batch 2x/an.", deadline: "Mars & Sept", success: "Airbnb, Stripe, Dropbox" },
  { name: "Station F", url: "https://stationf.co", location: "Paris, France", description: "Plus grand campus de startups au monde. 30+ programmes.", deadline: "Continu", success: "Alan, Doctolib" },
  { name: "Techstars", url: "https://www.techstars.com", location: "Global", description: "Réseau mondial d'accélérateurs. 150+ programmes.", deadline: "Variable", success: "SendGrid, DigitalOcean" },
  { name: "500 Global", url: "https://500.co", location: "San Francisco, USA", description: "Accélérateur avec réseau mondial de mentors.", deadline: "Continu", success: "Canva, Grab" },
  { name: "The Family (Hexa)", url: "https://www.hexa.cc", location: "Paris, France", description: "Startup studio européen, écosystème Hexa.", deadline: "Continu", success: "Qonto, Ankorstore" },
  { name: "Plug and Play", url: "https://www.plugandplaytechcenter.com", location: "Sunnyvale, USA", description: "Plateforme d'innovation et d'accélération corporate.", deadline: "Continu", success: "PayPal, Dropbox" },
  { name: "NUMA", url: "https://www.numa.co", location: "Paris, France", description: "Accélérateur français spécialisé open innovation.", deadline: "Variable", success: "+200 startups accélérées" },
];

const VC_FIRMS: { scope: string; firms: { name: string; url: string; stage: string; sectors: string }[] }[] = [
  {
    scope: "🇫🇷 France",
    firms: [
      { name: "Partech", url: "https://partechpartners.com", stage: "Seed → Growth", sectors: "Tech, SaaS, Fintech" },
      { name: "Bpifrance", url: "https://www.bpifrance.fr", stage: "Amorçage → Growth", sectors: "Tous secteurs" },
      { name: "Kima Ventures", url: "https://www.kimaventures.com", stage: "Pre-seed / Seed", sectors: "Tech généraliste" },
      { name: "Elaia Partners", url: "https://www.elaia.com", stage: "Seed → Series B", sectors: "Deep Tech, SaaS" },
      { name: "Idinvest (Eurazeo)", url: "https://www.eurazeo.com", stage: "Series A → Growth", sectors: "Tech, Santé" },
      { name: "Serena Capital", url: "https://www.serena.vc", stage: "Seed → Series B", sectors: "SaaS, Data, IA" },
    ],
  },
  {
    scope: "🇪🇺 Europe",
    firms: [
      { name: "Atomico", url: "https://www.atomico.com", stage: "Series A → Growth", sectors: "Tech, Deep Tech" },
      { name: "Index Ventures", url: "https://www.indexventures.com", stage: "Seed → Growth", sectors: "Tech, Fintech, SaaS" },
      { name: "Balderton Capital", url: "https://www.balderton.com", stage: "Series A → Growth", sectors: "Tech européenne" },
      { name: "Northzone", url: "https://northzone.com", stage: "Seed → Growth", sectors: "Fintech, SaaS, Marketplace" },
      { name: "HV Capital", url: "https://www.hvcapital.com", stage: "Series A → C", sectors: "Tech, e-commerce" },
    ],
  },
  {
    scope: "🌍 Mondial",
    firms: [
      { name: "Sequoia Capital", url: "https://www.sequoiacap.com", stage: "Seed → Growth", sectors: "Tech, IA, SaaS" },
      { name: "Andreessen Horowitz (a16z)", url: "https://a16z.com", stage: "Seed → Growth", sectors: "Tech, Crypto, Bio" },
      { name: "Accel", url: "https://www.accel.com", stage: "Seed → Growth", sectors: "SaaS, Fintech, Infra" },
      { name: "Lightspeed Venture Partners", url: "https://lsvp.com", stage: "Seed → Growth", sectors: "Enterprise, Consumer" },
      { name: "Tiger Global", url: "https://www.tigerglobal.com", stage: "Growth", sectors: "Tech, Internet" },
      { name: "SoftBank Vision Fund", url: "https://visionfund.com", stage: "Late Stage", sectors: "IA, Tech, Mobilité" },
    ],
  },
];

const USEFUL_DOCS = [
  { label: "Pitch Deck", icon: FileText, desc: "Présentation investisseurs (10-15 slides)" },
  { label: "Executive Summary", icon: FileText, desc: "Résumé exécutif (1-2 pages)" },
  { label: "Business Plan", icon: FileText, desc: "Plan d'affaires complet" },
  { label: "Financial Projections", icon: TrendingUp, desc: "Prévisionnel financier 3-5 ans" },
  { label: "Cap Table", icon: Users, desc: "Table de capitalisation" },
  { label: "Term Sheet Template", icon: FileText, desc: "Modèle de term sheet" },
];

/* ───── component ───── */

export function FundraisingTab() {
  const [searchVC, setSearchVC] = useState("");
  const [activeScope, setActiveScope] = useState("all");

  const filteredVC = VC_FIRMS.map(group => ({
    ...group,
    firms: group.firms.filter(f =>
      f.name.toLowerCase().includes(searchVC.toLowerCase()) ||
      f.sectors.toLowerCase().includes(searchVC.toLowerCase())
    ),
  })).filter(g => activeScope === "all" || g.scope.includes(activeScope === "fr" ? "France" : activeScope === "eu" ? "Europe" : "Mondial"));

  return (
    <div className="space-y-6">

      {/* ── Pre-seed funds matched on profile ── */}
      <PreSeedFundsSection />

      {/* ── Plateformes de financement ── */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Landmark className="h-4 w-4 text-primary" />
            Plateformes de Financement
          </CardTitle>
          <CardDescription>Crowdfunding, equity et investissement participatif</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {PLATFORMS.map(p => (
              <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer"
                className="group border border-border rounded-lg p-3 hover:border-primary/40 hover:bg-primary/5 transition-colors">
                <div className="flex items-start justify-between mb-1">
                  <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{p.name}</span>
                  <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <p className="text-xs text-muted-foreground mb-2">{p.description}</p>
                <div className="flex gap-1.5">
                  <Badge variant="outline" className="text-[10px] border-primary/30">{p.type}</Badge>
                  <Badge variant="outline" className="text-[10px]">{p.scope}</Badge>
                </div>
              </a>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Documents utiles ── */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Upload className="h-4 w-4 text-primary" />
                Documents pour Investisseurs
              </CardTitle>
              <CardDescription>Préparez et téléchargez vos documents clés</CardDescription>
            </div>
            <Button size="sm" variant="outline" className="gap-1.5">
              <Upload className="h-3.5 w-3.5" />Importer un document
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {USEFUL_DOCS.map(doc => (
              <div key={doc.label} className="border border-dashed border-border rounded-lg p-4 flex flex-col items-center justify-center gap-2 hover:border-primary/40 hover:bg-primary/5 transition-colors cursor-pointer min-h-[100px]">
                <doc.icon className="h-6 w-6 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">{doc.label}</span>
                <span className="text-[10px] text-muted-foreground text-center">{doc.desc}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Incubateurs ── */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Rocket className="h-4 w-4 text-primary" />
            Incubateurs & Accélérateurs
          </CardTitle>
          <CardDescription>Postulez aux meilleurs programmes mondiaux</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {INCUBATORS.map(inc => (
              <a key={inc.name} href={inc.url} target="_blank" rel="noopener noreferrer"
                className="group flex items-start gap-4 border border-border rounded-lg p-3 hover:border-primary/40 hover:bg-primary/5 transition-colors">
                <div className="shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Rocket className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{inc.name}</span>
                    <ArrowUpRight className="h-3 w-3 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground mb-1.5">{inc.description}</p>
                  <div className="flex flex-wrap gap-1.5">
                    <Badge variant="outline" className="text-[10px]">
                      <MapPin className="h-2.5 w-2.5 mr-0.5" />{inc.location}
                    </Badge>
                    <Badge variant="outline" className="text-[10px] border-primary/30">
                      Deadline: {inc.deadline}
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">
                      <Star className="h-2.5 w-2.5 mr-0.5" />{inc.success}
                    </Badge>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Capital-Risque ── */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <BriefcaseBusiness className="h-4 w-4 text-primary" />
                Capital-Risque (VC)
              </CardTitle>
              <CardDescription>Identifiez les fonds d'investissement adaptés</CardDescription>
            </div>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input placeholder="Rechercher un VC ou secteur…" value={searchVC} onChange={e => setSearchVC(e.target.value)}
                  className="pl-8 h-8 text-xs w-48" />
              </div>
              <div className="flex rounded-md border border-border overflow-hidden">
                {[
                  { key: "all", label: "Tous" },
                  { key: "fr", label: "🇫🇷" },
                  { key: "eu", label: "🇪🇺" },
                  { key: "world", label: "🌍" },
                ].map(s => (
                  <button key={s.key}
                    onClick={() => setActiveScope(s.key)}
                    className={`px-2.5 py-1 text-xs transition-colors ${activeScope === s.key ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-muted-foreground"}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {filteredVC.map(group => (
            group.firms.length > 0 && (
              <div key={group.scope}>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{group.scope}</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {group.firms.map(f => (
                    <a key={f.name} href={f.url} target="_blank" rel="noopener noreferrer"
                      className="group border border-border rounded-lg p-3 hover:border-primary/40 hover:bg-primary/5 transition-colors">
                      <div className="flex items-start justify-between mb-1">
                        <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{f.name}</span>
                        <ArrowUpRight className="h-3 w-3 text-muted-foreground" />
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        <Badge variant="outline" className="text-[10px] border-primary/30">{f.stage}</Badge>
                        <Badge variant="outline" className="text-[10px]">{f.sectors}</Badge>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )
          ))}
          {filteredVC.every(g => g.firms.length === 0) && (
            <p className="text-sm text-muted-foreground text-center py-6">Aucun résultat pour "{searchVC}"</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
